import type { SendFile } from './types';

/**
 * Planurile și documentele cererii (arhitectură, cofraj, liste de cantități):
 * țin de toată cererea și pleacă la toți furnizorii. Atașate la mail când sunt
 * mici, altfel ca linkuri de descărcare (vezi `ATTACH_MAX_BYTES`).
 */

/** Un plan pus la cerere; aceeași formă ca documentele furnizorului. */
export type RfqFile = SendFile;

/**
 * Ce se poate pune, după extensie: browserele raportează DWG, ZIP și RAR sub
 * nume diferite (sau deloc), deci extensia decide, iar tipul îl punem noi.
 */
export const PLAN_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  zip: 'application/zip',
  rar: 'application/vnd.rar',
  '7z': 'application/x-7z-compressed',
  dwg: 'image/vnd.dwg',
  dxf: 'image/vnd.dxf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
};

/** Pentru `<input accept>`. */
export const PLAN_ACCEPT = Object.keys(PLAN_TYPES)
  .map((e) => `.${e}`)
  .join(',');

/** Extensia acceptată a unui nume de fișier, cu litere mici; null = nu se acceptă. */
export function planExt(filename: string): string | null {
  const ext = filename.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  return ext && PLAN_TYPES[ext] ? ext : null;
}

export const MAX_PLAN_BYTES = 50 * 1024 * 1024;
export const MAX_PLANS = 20;

/**
 * Până la atât, în total, planurile pleacă atașate la mail; peste, ca linkuri.
 * Limita uzuală a serverelor de mail e 20–25 MB, iar un atașament crește cu o
 * treime pe drum; 10 MB trece și prin cele mai stricte.
 */
export const ATTACH_MAX_BYTES = 10 * 1024 * 1024;

export const attachable = (files: Pick<RfqFile, 'size'>[]): boolean =>
  files.reduce((n, f) => n + f.size, 0) <= ATTACH_MAX_BYTES;

/**
 * Planurile puse după ce cererea a plecat la furnizor: el nu le-a primit.
 * `sentAt` null = n-a plecat încă, deci le va primi pe toate.
 */
export function plansMissed<F extends Pick<RfqFile, 'createdAt'>>(
  files: F[],
  sentAt: string | null,
): F[] {
  if (!sentAt) return [];
  const t = new Date(sentAt).getTime();
  return files.filter((f) => new Date(f.createdAt).getTime() > t);
}

/** Un plan pe pagina furnizorului și în mail: numele, mărimea, unde se descarcă. */
export interface PublicPlan {
  id: string;
  filename: string;
  size: number;
  url: string;
}

/** `…/api/public/oferta/<token>/fisiere/<id>`: descărcarea fără cont, prin linkul furnizorului. */
export const publicPlanUrl = (base: string, token: string, fileId: string): string =>
  `${base.replace(/\/+$/, '')}/api/public/oferta/${token}/fisiere/${fileId}`;
